import { facetBuilderService } from "./facet-builder";
import {
  PRODUCT_COLLECTION,
  getCatalogLang,
  includeFields,
  localizeHit,
  queryByFields,
} from "../utils/collection-utils";
import { escapeTypesenseValue } from "../utils/filter-fields";
import { getMultiSearchResults } from "../utils/multi-search";
import useAuthStore from "@/store/use-auth-store";

export interface SingleInstantSearchParams {
  query?: string;
  categoryId?: string; // 🆕 Kategori ID - categoryId filter'ı otomatik olarak eklenir
  facetFilters?: Record<string, string[]>; // 🆕 Format: { attributeId: [valueId1, valueId2] }
  productIds?: string[]; // 🆕 Product ID listesi - Bu ID'lerle INCLUDE filtreleme yapılır (exclude için client-side filter kullanın)
  offerIds?: string[]; // Backend'in seçtiği exact offer ID'leri
  excludeFamilyId?: string; // 🆕 Family ID hariç tutma - Similar products için aynı family'yi exclude eder
  groupBy?: string; // 🆕 Group by field (örn: "familyId" veya "categoryId") - Similar products için family bazlı, Discover more için category bazlı gruplama
  groupLimit?: number; // 🆕 Group limit - Her group'dan kaç örnek alınacak (örn: 1 veya 2)
  page?: number;
  perPage?: number;
  sortBy?: string; // 🆕 Custom sort_by (örn: "_random" veya "effectivePrice:desc")
  overrides?: Partial<{
    apiUrl: string;
    authHeader: string;
    apiKey: string;
    collection: string;
    queryBy: string[];
  }>;
  language?: string; // Kullanıcının aktif dili (UI store adı, örn "Turkish") — merged koleksiyonda alan son ekini belirler
  region?: string; // Kullanıcının aktif bölgesi — countries filtrelemesi için
  signal?: AbortSignal;
}

export interface SingleInstantSearchResponse<T = any> {
  hits: T[];
  totalHits: number;
  page: number;
  perPage: number;
  hasMore: boolean;
  raw?: any;
}

/** Fields the product cards need (base names, localized per language). */
export const CARD_INCLUDE_FIELDS =
  "id,offerId,productId,familyId,attributes,categoryId,displayName,effectivePrice,prices,imageUrl,images,brand,merchant,seller,inStock,canonicalUrl,sourceUrl.productId";

function getDefaultConfig(language?: string) {
  return {
    apiUrl: `${import.meta.env.VITE_TYPESENSE_API_URL}multi_search`,
    authHeader: "X-TYPESENSE-API-KEY",
    apiKey: import.meta.env.VITE_TYPESENSE_API_KEY as string,
    collection: PRODUCT_COLLECTION as string,
    queryBy: queryByFields(getCatalogLang(language), "main").split(","),
  };
}

/**
 * Builds the single document search sent by `singleInstantSearch`.
 * Exported so the request shape can be tested without a network.
 */
export function buildSingleSearchRequest(
  params: SingleInstantSearchParams & { collection: string; queryBy: string[] },
  filterBy: string,
): Record<string, unknown> {
  const {
    query = "*",
    page = 1,
    perPage = 20,
    sortBy,
    groupBy,
    groupLimit,
    language,
    collection,
    queryBy,
  } = params;
  const lang = getCatalogLang(language);

  return {
    collection,
    q: query || "*",
    query_by: queryBy.join(","),
    page,
    per_page: perPage,
    filter_by: filterBy || "",
    sort_by: sortBy || "effectivePrice:desc", // ✅ Custom sort_by veya default
    ...(groupBy ? { group_by: groupBy } : {}), // 🆕 Group by field (örn: "familyId")
    ...(groupLimit ? { group_limit: groupLimit } : {}), // 🆕 Group limit (örn: 1)
    facet_by: "", // ✅ Facet'lere gerek yok
    facet_return_parent: "",
    exhaustive_search: false,
    highlight_fields: "none",
    exclude_fields: "search_time_ms,out_of",
    drop_tokens_threshold: 0,
    // ✅ Include gerekli field'lar (ProductCard için gerekli) + familyId for grouping
    include_fields: includeFields(lang, CARD_INCLUDE_FIELDS),
    use_cache: false,
    group_by: groupBy || "",
    group_limit: groupLimit || 1,
    cache_ttl: 60,
  };
}

export async function singleInstantSearch<T = any>(
  params: SingleInstantSearchParams = {},
): Promise<SingleInstantSearchResponse<T>> {
  const {
    categoryId, // 🆕 Kategori ID
    facetFilters = {},
    productIds, // 🆕 Product ID listesi
    offerIds,
    excludeFamilyId, // 🆕 Family ID hariç tutma
    groupBy, // 🆕 Group by field
    page = 1,
    perPage = 20,
    language,
    region,
    overrides,
    signal,
  } = params;

  const lang = getCatalogLang(language);
  const cfg = { ...getDefaultConfig(language), ...(overrides || {}) };

  // 🆕 User region — use-instantsearch-integration ile aynı pattern (Zustand getState ile hook dışında erişim)
  const userRegion = (region ?? useAuthStore.getState().user?.region ?? "").trim();

  // 🆕 Facet filtreleri ID-based Typesense formatına çevir
  const attributeFilterBy = facetBuilderService.buildFilterBy(facetFilters);

  // 🆕 Countries filter — use-instantsearch-integration ile aynı pattern
  let filterBy = attributeFilterBy;
  if (userRegion) {
    const countriesFilter = `countries:${userRegion}`;
    if (filterBy) {
      filterBy = `${countriesFilter} && ${filterBy}`;
    } else {
      filterBy = countriesFilter;
    }
  }

  // 🆕 Category ID filter ekle (varsa)
  if (categoryId) {
    // Typesense categoryId filtreleme: categoryId:=xxx
    const categoryFilter = `categoryId:=${categoryId}`;
    if (filterBy) {
      // Attribute filtreleri varsa AND ile birleştir
      filterBy = `${filterBy} && ${categoryFilter}`;
    } else {
      // Sadece category filter varsa
      filterBy = categoryFilter;
    }
  }

  // 🆕 Product ID filtreleri ekle (varsa) - Include specific products
  // NOT: productIds parametresi include için kullanılıyor, exclude için client-side filter yapılmalı
  // Typesense'de NOT operatörü syntax'ı karmaşık ve hata verebilir, bu yüzden client-side filter tercih ediliyor
  const normalizedOfferIds = offerIds?.map((id) => id.trim()) ?? [];
  const hasExactOfferIds =
    normalizedOfferIds.length > 0 && normalizedOfferIds.every(Boolean);
  const exactIds = hasExactOfferIds ? normalizedOfferIds : productIds;
  const exactIdField = hasExactOfferIds ? "offerId" : "productId";
  if (exactIds && exactIds.length > 0) {
    // Typesense productId filtreleme: productId:=xxx || productId:=yyy
    // Backend'den gelen ID'ler productId field'ına karşılık geliyor
    const idFilters = exactIds
      .map((id) => `${exactIdField}:=${escapeTypesenseValue(id)}`)
      .join(" || ");
    if (filterBy) {
      // Mevcut filtreler varsa AND ile birleştir (parantez gerekli)
      filterBy = `${filterBy} && (${idFilters})`;
    } else {
      // Sadece ID filtreleri varsa parantez gereksiz
      filterBy = idFilters;
    }
  }

  // 🆕 Family ID exclude filtreleme (varsa) - Similar products için aynı family'yi hariç tut
  if (excludeFamilyId) {
    // Typesense familyId exclude: familyId:!=xxx
    const familyExcludeFilter = `familyId:!=${excludeFamilyId}`;
    if (filterBy) {
      // Mevcut filtreler varsa AND ile birleştir
      filterBy = `${filterBy} && ${familyExcludeFilter}`;
    } else {
      // Sadece family exclude filter varsa
      filterBy = familyExcludeFilter;
    }
  }

  // Sadece document search — facet keşfi ve facet araması bu servisten kaldırıldı
  // (hiçbir çağıran kullanmıyordu; `attributes.name` facet'i merged koleksiyonda yok).
  const searches = [
    buildSingleSearchRequest(
      { ...params, collection: cfg.collection, queryBy: cfg.queryBy },
      filterBy,
    ),
  ];

  const response = await fetch(cfg.apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      [cfg.authHeader]: cfg.apiKey,
    },
    body: JSON.stringify({ searches }),
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `Typesense API Error: ${response.status} - ${response.statusText}`,
    );
  }

  const data = await response.json();
  const searchResult = getMultiSearchResults(data)[0] || {};

  let documents: T[];
  let totalHits: number;

  // 🆕 Group by kullanıldığında grouped_hits gelir, normal durumda hits gelir
  if (groupBy && searchResult.grouped_hits) {
    // Gruplu response: Her gruptaki tüm hit'leri flatten et (group_limit > 1 destek)
    const groupedHits = searchResult.grouped_hits || [];
    documents = groupedHits.flatMap((group: any) =>
      (group.hits || [])
        .map((hit: any) => hit?.document)
        .filter(Boolean)
        .map((doc: any) => localizeHit(doc, lang)),
    ) as T[];

    // Grouped response'da found, grup sayısını gösterir
    totalHits = searchResult.found || groupedHits.length;
  } else {
    // Normal response: Direkt hits array'i
    documents = (searchResult.hits || []).map((hit: any) =>
      localizeHit(hit.document, lang),
    ) as T[];
    totalHits = searchResult.found || 0;
  }

  return {
    hits: documents,
    totalHits,
    page,
    perPage,
    hasMore: page * perPage < totalHits,
    raw: data,
  };
}
